import React from 'react';
import { COMPONENT_LIBRARY } from '../../engine/componentLibrary';
import { ComponentDefinition, AgeMode, ExperimentNodeData } from '../../types';
import { useSimulatorStore } from '../../store/simulatorStore';

interface Props {
  ageMode: AgeMode;
}

const CATEGORY_LABELS: Record<string, string> = {
  source: '⚛️ Sources',
  device: '🔬 Devices',
  output: '📈 Outputs',
};

export default function ComponentLibrary({ ageMode }: Props) {
  const { addNode, nodes } = useSimulatorStore();

  const filtered = COMPONENT_LIBRARY.filter((c) =>
    c.ageMode.includes(ageMode)
  );

  const grouped = filtered.reduce<Record<string, ComponentDefinition[]>>(
    (acc, comp) => {
      if (!acc[comp.category]) acc[comp.category] = [];
      acc[comp.category].push(comp);
      return acc;
    },
    {}
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    comp: ComponentDefinition
  ) => {
    e.dataTransfer.setData('componentType', comp.type);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = (comp: ComponentDefinition) => {
    const id = `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const offset = nodes.length * 30;
    addNode({
      id,
      type: 'experimentNode',
      position: { x: 200 + offset, y: 150 + offset },
      data: {
        componentType: comp.type,
        label: comp.label,
        category: comp.category,
        icon: comp.icon,
        description: comp.description,
        tooltip: comp.tooltip,
        ageMode: comp.ageMode,
        properties: { ...comp.defaultProperties },
        inputs: comp.inputs,
        outputs: comp.outputs,
      } as ExperimentNodeData,
    });
  };

  return (
    <div
      style={{
        width: 200,
        background: '#f8fafc',
        borderRight: '1px solid #e2e8f0',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          padding: '14px 12px 8px',
          borderBottom: '1px solid #e2e8f0',
          background: 'white',
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Components
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
          Click or drag onto workspace
        </div>
      </div>

      {(['source', 'device', 'output'] as const).map((cat) =>
        grouped[cat] ? (
          <div key={cat}>
            <div
              style={{
                padding: '10px 12px 4px',
                fontSize: 10,
                fontWeight: 700,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {CATEGORY_LABELS[cat]}
            </div>
            {grouped[cat].map((comp) => (
              <ComponentCard
                key={comp.type}
                comp={comp}
                onDragStart={handleDragStart}
                onClick={() => handleClick(comp)}
              />
            ))}
          </div>
        ) : null
      )}
    </div>
  );
}

function ComponentCard({
  comp,
  onDragStart,
  onClick,
}: {
  comp: ComponentDefinition;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, comp: ComponentDefinition) => void;
  onClick: () => void;
}) {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, comp)}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        margin: '3px 8px',
        padding: '8px 10px',
        borderRadius: 8,
        background: hovered ? comp.bgColor : 'white',
        border: `1.5px solid ${hovered ? comp.color : '#e2e8f0'}`,
        cursor: 'grab',
        transition: 'all 0.12s',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        userSelect: 'none',
      }}
      title={comp.tooltip}
    >
      <span style={{ fontSize: 16, flexShrink: 0 }}>{comp.icon}</span>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>
          {comp.label}
        </div>
        <div style={{ fontSize: 9, color: '#64748b', lineHeight: 1.3 }}>
          {comp.description.length > 32
            ? comp.description.slice(0, 32) + '…'
            : comp.description}
        </div>
      </div>
    </div>
  );
}
